import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, MessageSquare, User } from "lucide-react";
import Logo from "@/components/Logo";
import AdSlot from "@/components/AdSlot";
import { api, absUrl, formatApiError } from "@/lib/api";
import { CATEGORIES, timeAgo } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { CheckCircle2 } from "lucide-react";

export default function Community() {
    const [category, setCategory] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = category ? { category } : {};
            const { data } = await api.get("/posts", { params });
            setItems(data.items);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [category]);

    useEffect(() => {
        load();
    }, [load]);

    async function vote(post, direction) {
        if (!user) return navigate("/login");
        const nextDir = post.my_vote === direction ? 0 : direction;
        try {
            const { data } = await api.post(`/posts/${post.post_id}/vote`, { direction: nextDir });
            setItems((cur) =>
                cur.map((p) =>
                    p.post_id === post.post_id
                        ? { ...p, upvotes: data.upvotes, my_vote: data.my_vote }
                        : p,
                ),
            );
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="min-h-screen bg-onyx-900">
            <Seo
                title="SRT Hellcat &amp; Mopar Community — Build Threads, Tech Help, Meets"
                description="Join the SRT Finder community — a Reddit-style forum for Dodge Hellcat, SRT Redeye, Scat Pack, SRT Demon, Chrysler 300 SRT8, and RAM TRX owners. Post builds, ask tech questions, find meets and events, and talk to real Mopar performance enthusiasts."
                canonicalPath="/community"
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Community", path: "/community" },
                ]}
            />
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Logo size="sm" />
                    <div className="flex-1" />
                    {user && (
                        <Link
                            to="/community/new"
                            data-testid="new-post-btn"
                            className="btn-red px-4 py-2 text-xs font-bold"
                        >
                            New Post
                        </Link>
                    )}
                </div>
                <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
                    <CatChip
                        label="All"
                        active={!category}
                        onClick={() => setCategory("")}
                        testid="cat-all"
                    />
                    {CATEGORIES.map((c) => (
                        <CatChip
                            key={c.value}
                            label={c.short}
                            active={category === c.value}
                            onClick={() => setCategory(c.value)}
                            testid={`cat-${c.value}`}
                        />
                    ))}
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 py-4 space-y-3">
                <AdSlot placement="community-inline" />
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="surface rounded-md p-5">
                            <div className="h-4 w-1/2 bg-onyx-800 rounded animate-pulse mb-3" />
                            <div className="h-3 w-full bg-onyx-800 rounded animate-pulse mb-2" />
                            <div className="h-3 w-2/3 bg-onyx-800 rounded animate-pulse" />
                        </div>
                    ))
                ) : items.length === 0 ? (
                    <div className="surface rounded-md p-10 text-center">
                        <div className="headline text-xl">No posts yet</div>
                        <div className="text-sm text-onyx-500 mt-1">Be the first to post in this category.</div>
                    </div>
                ) : (
                    items.map((p, i) => (
                        <motion.article
                            key={p.post_id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(i * 0.03, 0.3) }}
                            className="surface rounded-md p-4 flex gap-3"
                        >
                            <div className="flex flex-col items-center gap-1 shrink-0">
                                <button
                                    data-testid={`upvote-${p.post_id}`}
                                    onClick={() => vote(p, 1)}
                                    className={`p-1 rounded-sm ${p.my_vote === 1 ? "text-hellcat" : "text-onyx-500 hover:text-white"}`}
                                >
                                    <ArrowUp className="w-5 h-5" strokeWidth={p.my_vote === 1 ? 3 : 2} />
                                </button>
                                <div
                                    className={`text-sm font-bold ${p.my_vote === 1 ? "text-hellcat" : "text-white"}`}
                                >
                                    {p.upvotes}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-xs text-onyx-500 mb-1">
                                    <span
                                        className="rounded-sm px-1.5 py-0.5 bg-onyx-800 uppercase tracking-widest text-[10px]"
                                        style={{ letterSpacing: "0.15em" }}
                                    >
                                        {CATEGORIES.find((c) => c.value === p.category)?.short}
                                    </span>
                                    <span>·</span>
                                    <span className="truncate">{p.author?.name}</span>
                                    <span>·</span>
                                    <span>{timeAgo(p.created_at)}</span>
                                </div>
                                <Link to={`/community/${p.post_id}`} data-testid={`post-link-${p.post_id}`}>
                                    <h3 className="headline text-lg text-white hover:text-hellcat transition-colors leading-snug inline-flex items-baseline gap-2">
                                        {p.solved && (
                                            <span
                                                className="chip"
                                                style={{
                                                    background: "rgba(74,222,128,0.14)",
                                                    borderColor: "rgba(74,222,128,0.4)",
                                                    color: "#86efac",
                                                    fontSize: "10px",
                                                    padding: "2px 8px",
                                                }}
                                                data-testid={`solved-badge-${p.post_id}`}
                                            >
                                                <CheckCircle2 className="w-3 h-3" /> SOLVED
                                            </span>
                                        )}
                                        <span>{p.title}</span>
                                    </h3>
                                </Link>
                                {p.body && (
                                    <p className="text-sm text-onyx-500 mt-1 line-clamp-2">
                                        {p.body}
                                    </p>
                                )}
                                {p.photos?.length > 0 && (
                                    <div className="mt-3 rounded-sm overflow-hidden max-h-72 relative surface-elevated">
                                        <img
                                            src={absUrl(p.photos[0])}
                                            alt=""
                                            className="w-full max-h-72 object-cover"
                                        />
                                    </div>
                                )}
                                <div className="mt-3 flex items-center gap-4 text-xs text-onyx-500">
                                    <Link
                                        to={`/community/${p.post_id}`}
                                        className="flex items-center gap-1 hover:text-white"
                                    >
                                        <MessageSquare className="w-3.5 h-3.5" />
                                        {p.comment_count} comments
                                    </Link>
                                    {p.author?.user_id && (
                                        <Link
                                            to={`/users/${p.author.user_id}`}
                                            className="flex items-center gap-1 hover:text-white"
                                        >
                                            <User className="w-3.5 h-3.5" />
                                            {p.author.name}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </motion.article>
                    ))
                )}
            </div>
        </div>
    );
}

function CatChip({ label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`chip ${active ? "chip-active" : ""}`}
        >
            {label}
        </button>
    );
}
